import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetActivitiesQuery, useDeleteActivityMutation, useToggleActivityPremiumMutation, useMassActionMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import DateRangePicker from '../../../components/DateRangePicker';
import Pagination from '../../../components/Pagination';
import { confirmDelete } from '../../../utils/confirm';
import { showSuccess, showError } from '../../../utils/toast';

// Format date as "d M, Y h:i A" (e.g., "02 Jun, 2026 01:00 AM")
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = (hours % 12 || 12).toString().padStart(2, '0');
  return `${day} ${month}, ${year} ${displayHours}:${minutes} ${ampm}`;
};

const AdminActivities: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const { data, isLoading, error, refetch } = useGetActivitiesQuery({ search, daterange, page });
  const [deleteActivity] = useDeleteActivityMutation();
  const [toggleActivityPremium] = useToggleActivityPremiumMutation();
  const [massAction] = useMassActionMutation();

  // Reset to page 1 when search or daterange changes
  useEffect(() => {
    setPage(1);
  }, [search, daterange]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteActivity(id).unwrap();
      showSuccess(result.message || 'Activity deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete activity. Please try again.');
    }
  };

  const handleTogglePremium = async (id: number, currentPremium: number) => {
    try {
      const newPremium = currentPremium === 1 ? 0 : 1;
      const result = await toggleActivityPremium({ id, is_premium: newPremium }).unwrap();
      showSuccess(result.message || 'Premium status updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update premium status. Please try again.');
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allIds = activities.data.map((activity: any) => activity.id);
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id));
      setSelectAll(false);
    }
  };

  const handleBulkAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkAction || selectedIds.length === 0) {
      showError('Please select an action and at least one activity');
      return;
    }

    if (bulkAction === 'delete') {
      const confirmed = await confirmDelete('Are you sure?', "You won't be able to revert this!");
      if (!confirmed) return;
    }

    try {
      const result = await massAction({
        type: 'activity',
        action: bulkAction,
        selectedIds: selectedIds.join(','),
      }).unwrap();
      showSuccess(result.message || 'Bulk action completed successfully!');
      setSelectedIds([]);
      setSelectAll(false);
      setBulkAction('');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to perform bulk action. Please try again.');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading activities</div>;
  }

  const activities = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 15 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Activities</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Activities</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <a href="#" className="btn btn-base btn-sm" onClick={(e) => { e.preventDefault(); navigate('/admin/activities/add'); }}>
                Add New Activity
              </a>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15 me-5"
                        placeholder="Search Activity Title.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <div className="position-relative d-flex align-items-center">
                        <button type="submit" className="btn w-120px btn-base mx-3">Filter</button>
                        {(search || daterange) && (
                          <a href="#" onClick={(e) => { e.preventDefault(); setSearch(''); setDaterange(''); }} className="btn btn-sm btn-light-primary me-3">
                            Clear
                          </a>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="card-body pt-0">
                <form onSubmit={handleBulkAction} className="d-flex mb-3">
                  <select
                    name="action"
                    required
                    className="form-control w-80px p-2 me-2"
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                  >
                    <option value="">Action</option>
                    <option value="delete">Delete</option>
                  </select>
                  <button className="btn btn-base btn-sm p-3" type="submit">Confirm</button>
                </form>
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="w-10px pe-2">
                          <input
                            className="form-check-input border-base mt-2"
                            type="checkbox"
                            id="checkAll"
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>#</th>
                        <th>Image</th>
                        <th>Title</th>
                        <th>Country</th>
                        <th>Activity DateTime</th>
                        <th>Is Premium</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {activities.data.map((activity: any, index: number) => (
                        <tr key={activity.id}>
                          <td>
                            <input
                              className="form-check-input border-base mt-2 action_ids"
                              type="checkbox"
                              value={activity.id}
                              checked={selectedIds.includes(activity.id)}
                              onChange={(e) => handleSelectItem(activity.id, e.target.checked)}
                            />
                          </td>
                          <td>{(activities.current_page - 1) * 15 + index + 1}</td>
                          <td><img src={activity.image || '/media/blank.png'} width="60" className="rounded" alt="" /></td>
                          <td>{activity.title}</td>
                          <td>{activity.country || '-'}</td>
                          <td>{formatDateTime(activity.activity_at)}</td>
                          <td>
                            <div className="form-check form-switch mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={activity.is_premium === 1}
                                onChange={() => handleTogglePremium(activity.id, activity.is_premium)}
                              />
                            </div>
                          </td>
                          <td className="text-center d-flex">
                            <a
                              href="#"
                              className="btn btn-sm h-30px btn-light-primary me-1"
                              onClick={(e) => { e.preventDefault(); navigate(`/admin/activities/${activity.id}`); }}
                            >
                              <i className="fas fa-eye"></i>
                            </a>
                            <a
                              href="#"
                              className="btn btn-sm h-30px btn-light-primary me-1"
                              onClick={(e) => { e.preventDefault(); navigate(`/admin/activities/${activity.id}/edit`); }}
                            >
                              <i className="fas fa-pencil"></i>
                            </a>
                            <button
                              type="button"
                              className="delBtn btn btn-sm btn-light-primary"
                              onClick={() => handleDelete(activity.id)}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {activities.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={activities.current_page}
                      lastPage={activities.last_page}
                      total={activities.total || 0}
                      perPage={activities.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminActivities;
